import React, { useState, useMemo, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { statusMeta } from '../mock/mock';
import { Search, Plus, Trash2, Mail, Phone, Edit2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';
import EditContactDialog from '../components/EditContactDialog';

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [campaignFilter, setCampaignFilter] = useState('all');
  const [editing, setEditing] = useState(null);

  const load = () => {
    api.get('/contacts', { params: { status: statusFilter, campaign_id: campaignFilter, search: search || undefined } }).then(r => setContacts(r.data)).catch(()=>{});
  };

  useEffect(() => {
    api.get('/campaigns').then(r => setCampaigns(r.data)).catch(()=>{});
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, campaignFilter]);
  useEffect(() => { const t = setTimeout(load, 300); return ()=>clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  const remove = async (id) => {
    await api.delete(`/contacts/${id}`);
    setContacts(contacts.filter(c => c.id !== id));
    toast.success('Contact removed');
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl">Contacts</h1>
          <p className="text-zinc-500 mt-1">{contacts.length} contacts across all campaigns</p>
        </div>
        <Button className="bg-[#d4a94a] hover:bg-[#e5bb5c] text-black font-medium" asChild><a href="/app/upload"><Plus size={16} className="mr-1"/>Import</a></Button>
      </div>

      <Card className="mt-6 bg-[#111110] border-zinc-900">
        <div className="p-4 border-b border-zinc-900 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"/>
            <Input placeholder="Search by name, email or company…" value={search} onChange={e=>setSearch(e.target.value)} className="pl-9 bg-[#0a0a0b] border-zinc-800 text-zinc-100 focus-visible:ring-[#d4a94a]"/>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[190px] bg-[#0a0a0b] border-zinc-800 text-zinc-100"><SelectValue/></SelectTrigger>
            <SelectContent className="bg-[#111110] border-zinc-800 text-zinc-100">
              <SelectItem value="all">All statuses</SelectItem>
              {Object.entries(statusMeta).map(([k,v])=><SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={campaignFilter} onValueChange={setCampaignFilter}>
            <SelectTrigger className="w-[200px] bg-[#0a0a0b] border-zinc-800 text-zinc-100"><SelectValue/></SelectTrigger>
            <SelectContent className="bg-[#111110] border-zinc-800 text-zinc-100">
              <SelectItem value="all">All campaigns</SelectItem>
              {campaigns.map(c=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-zinc-500 uppercase">
              <tr className="border-b border-zinc-900">
                <th className="text-left px-4 py-3 font-medium">Name</th>
                <th className="text-left px-4 py-3 font-medium">Contact</th>
                <th className="text-left px-4 py-3 font-medium">Company</th>
                <th className="text-left px-4 py-3 font-medium">Campaign</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-right px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-900">
              {contacts.map(c => {
                const m = statusMeta[c.status] || statusMeta.queued;
                const camp = campaigns.find(x=>x.id===c.campaign_id);
                const missing = c.missing_fields || [];
                return (
                  <tr
                    key={c.id}
                    className="row-hover cursor-pointer"
                    onClick={()=>setEditing(c)}
                    data-testid={`contact-row-${c.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="text-zinc-100">{c.first_name} {c.last_name}</div>
                      <div className="text-xs text-zinc-500">{c.job_title}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-zinc-300 text-xs"><Mail size={12}/>{c.email}</div>
                      {c.phone && <div className="flex items-center gap-1.5 text-zinc-500 text-xs mt-1"><Phone size={12}/>{c.phone}</div>}
                    </td>
                    <td className="px-4 py-3 text-zinc-300">{c.company}</td>
                    <td className="px-4 py-3 text-zinc-400 text-xs">{camp?.name || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={m.color}>{m.label}</Badge>
                        {missing.length > 0 && (
                          <Badge className="bg-orange-950 text-orange-300 border border-orange-900 text-[10px]" title={`Missing: ${missing.join(', ')}`}>
                            <AlertTriangle size={10} className="mr-1"/>{missing.length} missing
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right" onClick={e=>e.stopPropagation()}>
                      <div className="flex justify-end gap-3">
                        <button
                          onClick={()=>setEditing(c)}
                          className="text-zinc-500 hover:text-[#f5d67a]"
                          data-testid={`contact-edit-${c.id}`}
                        ><Edit2 size={14}/></button>
                        <button
                          onClick={()=>remove(c.id)}
                          className="text-zinc-500 hover:text-red-400"
                          data-testid={`contact-delete-${c.id}`}
                        ><Trash2 size={14}/></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {contacts.length===0 && (
                <tr><td colSpan={6} className="text-center py-12 text-zinc-500">No contacts yet. <a href="/app/upload" className="text-[#f5d67a] hover:underline">Import from CV or CSV</a></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
      <EditContactDialog
        open={!!editing}
        onOpenChange={(o)=>{ if (!o) setEditing(null); }}
        contact={editing}
        missingFields={editing?.missing_fields || []}
        onSaved={(updated)=>setContacts(cs=>cs.map(x=>x.id===updated.id ? updated : x))}
      />
    </div>
  );
}
